import React, { useEffect, useState } from 'react';
import { 
  Paper, 
  Grid, 
  Typography, 
  Box, 
  Chip, 
  Link,
  Alert
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { clientService } from '../../services/api/clientService';

// Convert blob storage URL to API download URL to avoid CORS issues
const convertToApiDownloadUrl = (blobUrl: string): string => {
  try {
    const url = new URL(blobUrl);
    const pathSegments = url.pathname.split('/').filter(Boolean);
    
    // Extract container and blob path from URL
    // URL format: https://storage.blob.core.windows.net/container/path/to/file
    const container = pathSegments[0];
    const blobPath = pathSegments.slice(1).join('/');
    
    // Use the API endpoint for downloading
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
    return `${baseUrl}/api/v1/storage/download?path=${encodeURIComponent(blobPath)}&container=${encodeURIComponent(container)}`;
  } catch {
    // If URL parsing fails, return the original URL
    return blobUrl;
  }
};

interface JobStatusPanelProps {
  status: {
    status: string;
    job_id?: string;
    updated_at?: string;
    created_at?: string;
    started_at?: string;
    completed_at?: string;
    message?: string;
    output_blob?: string;
    output_links?: { 
      categorized_keywords?: string;
      "results.csv"?: string;
    };
    workflow_id?: string;
    client_id?: string;
    record_count?: number;
    error_count?: number;
    success?: boolean;
  };
  jobId?: string;
  // Props for Snowflake status from parent
  isSavingToSnowflake?: boolean;
  snowflakeSaveSuccess?: boolean;
  snowflakeSaveError?: string | null;
}

const JobStatusPanel: React.FC<JobStatusPanelProps> = ({ 
  status, 
  jobId,
  isSavingToSnowflake = false,
  snowflakeSaveSuccess = false,
  snowflakeSaveError = null
}) => {
  const [clientName, setClientName] = useState<string | null>(null);

  // Effect to fetch client name from database
  useEffect(() => {
    const fetchClientName = async () => {
      if (status.client_id) {
        try {
          const clientId = parseInt(status.client_id, 10);
          if (!isNaN(clientId)) {
            const client = await clientService.getClient(clientId);
            setClientName(client.name);
            console.log(`Fetched client name: ${client.name} for client ID: ${clientId}`);
          }
        } catch (error) {
          console.error('Error fetching client name:', error);
          // Fallback to default format
          setClientName(`Client ${status.client_id}`);
        }
      }
    };

    fetchClientName();
  }, [status.client_id]);

  const getStatusColor = (status: string): string => {
    switch(status) {
      case 'completed':
      case 'finalized':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Status:</Typography>
          <Box sx={{ mt: 1 }}>
            <Chip 
              label={status.status} 
              color={getStatusColor(status.status) as "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"}
              sx={{ textTransform: 'capitalize' }}
            />
          </Box>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Job ID:</Typography>
          <Typography variant="body1">{status.job_id || jobId}</Typography>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Last Updated:</Typography>
          <Typography variant="body1">
            {status.updated_at ? new Date(status.updated_at).toLocaleString() : 'N/A'}
          </Typography>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Created:</Typography>
          <Typography variant="body1">
            {status.created_at ? new Date(status.created_at).toLocaleString() : 'N/A'}
          </Typography>
        </Grid>
        
        {status.started_at && (
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Started:</Typography>
            <Typography variant="body1">
              {new Date(status.started_at).toLocaleString()}
            </Typography>
          </Grid>
        )}
        
        {status.completed_at && (
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Completed:</Typography>
            <Typography variant="body1">
              {new Date(status.completed_at).toLocaleString()}
            </Typography>
          </Grid>
        )}
        
        {status.record_count !== undefined && (
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Records Processed:</Typography>
            <Typography variant="body1">{status.record_count}</Typography>
          </Grid>
        )}
        
        {status.error_count !== undefined && (
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Errors:</Typography>
            <Typography variant="body1" color={status.error_count > 0 ? "error" : "inherit"}>
              {status.error_count}
            </Typography>
          </Grid>
        )}
        
        {status.message && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Message:</Typography>
            <Typography variant="body1">
              {status.message}
            </Typography>
          </Grid>
        )}
        
        {status.output_blob && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Results:</Typography>
            <Link
              href={`/api/download/${status.output_blob}`}
              target="_blank"
              rel="noopener"
              sx={{ display: 'inline-block', mt: 1 }}
            >
              Download Results
            </Link>
          </Grid>
        )}
        
        {status.output_links?.categorized_keywords && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Categorized Keywords:</Typography>
            <Link
              href={convertToApiDownloadUrl(status.output_links.categorized_keywords)}
              target="_blank"
              rel="noopener"
              sx={{ display: 'inline-block', mt: 1 }}
            >
              Download Categorized Keywords
            </Link>
          </Grid>
        )}
        
        {/* Snowflake Save Status */}
        {(isSavingToSnowflake || snowflakeSaveSuccess || snowflakeSaveError) && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CloudUploadIcon sx={{ mr: 1 }} />
              Snowflake Integration:
            </Typography>
            
            {isSavingToSnowflake && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Saving results to Snowflake...
              </Alert>
            )}
            
            {snowflakeSaveSuccess && (
              <Alert severity="success" sx={{ mt: 1 }}>
                Results successfully saved to Snowflake
              </Alert>
            )}
            
            {snowflakeSaveError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Failed to save to Snowflake: {snowflakeSaveError}
              </Alert>
            )}
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default JobStatusPanel;
